import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { PlayArrow, DeleteOutline } from '@mui/icons-material';
import NavBar from '../components/NavBar';
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useSidebar } from '../contexts/SidebarContext';
import { useNavigate, useParams } from 'react-router-dom';
import ChromeStreamerCDP from '../components/ChromeStreamerCDP';
import { API_URL } from '../globals';
import { supabase } from '../supabaseClient';

// Test Results Display Component
const TestResults = ({ results, isRunning }) => {
  if (!results && !isRunning) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1A1A1A',
        color: 'rgba(255, 255, 255, 0.5)'
      }}>
        <Typography variant="body2">Run a test to see results</Typography>
      </Box>
    );
  }

  if (isRunning) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1A1A1A',
        color: '#E0E0E0'
      }}>
        <Typography variant="body2">Running test...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#1A1A1A'
    }}>
      {/* Results Header */}
      <Box sx={{
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: results.success ? '#1B5E20' : '#C62828',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Typography variant="body2" sx={{ 
          color: '#FFFFFF',
          fontWeight: 'bold',
          fontSize: '14px'
        }}>
          {results.success ? '✅ Test Passed' : '❌ Test Failed'}
        </Typography>
        {results.exit_code !== undefined && (
          <Typography variant="body2" sx={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '12px'
          }}>
            (Exit code: {results.exit_code})
          </Typography>
        )}
      </Box>

      {/* Results Content */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        padding: '16px',
        fontFamily: '"Fira Code", "Monaco", "Menlo", "Ubuntu Mono", monospace',
        fontSize: '12px',
        lineHeight: '1.4'
      }}>
        {results.stdout && (
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="body2" sx={{ 
              color: '#4CAF50',
              fontWeight: 'bold',
              marginBottom: 1
            }}>
              Output:
            </Typography>
            <pre style={{ 
              color: '#E0E0E0',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0
            }}>
              {results.stdout}
            </pre>
          </Box>
        )}

        {results.stderr && (
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="body2" sx={{ 
              color: '#F44336',
              fontWeight: 'bold',
              marginBottom: 1
            }}>
              Errors:
            </Typography>
            <pre style={{ 
              color: '#FFCDD2',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0
            }}>
              {results.stderr}
            </pre>
          </Box>
        )}

        {results.assertion_image && (
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="body2" sx={{ 
              color: '#2196F3',
              fontWeight: 'bold',
              marginBottom: 1
            }}>
              Assertion Screenshot:
            </Typography>
            <img 
              src={`data:image/png;base64,${results.assertion_image}`}
              alt="Test assertion screenshot"
              style={{
                maxWidth: '100%',
                height: 'auto',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '4px'
              }}
            />
          </Box>
        )}

        {results.error && (
          <Box>
            <Typography variant="body2" sx={{ 
              color: '#F44336',
              fontWeight: 'bold',
              marginBottom: 1
            }}>
              Error:
            </Typography>
            <pre style={{ 
              color: '#FFCDD2',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0
            }}>
              {results.error}
            </pre>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Custom code editor component - moved outside to prevent re-creation on each render
const CustomCodeEditor = ({ currentFile, codeContent, onCodeChange, onRunTest, onDeleteTestCase, isLoading, isRunning, isDeleting }) => {
  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#1A1A1A'
    }}>
      {/* Header */}
      <Box sx={{
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: '#262626',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="body2" sx={{ 
          color: '#E0E0E0',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          {isLoading ? 'Loading...' : currentFile}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlayArrow sx={{ fontSize: '16px' }} />}
            onClick={onRunTest}
            disabled={isLoading || isRunning}
            sx={{
              color: '#E0E0E0',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              fontSize: '12px',
              fontWeight: 'normal',
              textTransform: 'none',
              minWidth: 'auto',
              padding: '4px 12px',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              },
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {isRunning ? 'Running...' : 'Run Test'}
          </Button>

          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteOutline sx={{ fontSize: '16px' }} />}
            onClick={onDeleteTestCase}
            disabled={isLoading || isRunning || isDeleting}
            sx={{
              color: '#ff6b6b',
              borderColor: 'rgba(244, 67, 54, 0.4)',
              fontSize: '12px',
              fontWeight: 'normal',
              textTransform: 'none',
              minWidth: 'auto',
              padding: '4px 12px',
              '&:hover': {
                borderColor: 'rgba(244, 67, 54, 0.7)',
                backgroundColor: 'rgba(244, 67, 54, 0.08)'
              },
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </Box>

      {/* Code Area */}
      <Box sx={{ 
        flex: 1,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <textarea
          value={codeContent}
          onChange={onCodeChange}
          disabled={isLoading || isRunning}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            resize: 'none',
            backgroundColor: '#1A1A1A',
            color: (isLoading || isRunning) ? 'rgba(224, 224, 224, 0.5)' : '#E0E0E0',
            fontFamily: '"Fira Code", "Monaco", "Menlo", "Ubuntu Mono", monospace',
            fontSize: '14px',
            lineHeight: '1.5',
            padding: '16px',
            margin: 0,
            boxSizing: 'border-box',
            tabSize: 2,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word'
          }}
          placeholder="// Write your code here..."
          spellCheck={false}
        />
      </Box>
    </Box>
  );
};

function TestCase() {
  const { isOpen } = useSidebar();
  const navbarWidth = isOpen ? 240 : 64;
  const params = useParams();
  const caseId = params.caseId;
  const navigate = useNavigate();
  
  const [currentFile, setCurrentFile] = useState('main.js');
  const [codeContent, setCodeContent] = useState('// Loading test case...');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [testCaseDetails, setTestCaseDetails] = useState(null);
  const [apiBaseUrl, setApiBaseUrl] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTestCaseDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get authentication token
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData?.session?.access_token) {
        throw new Error('Authentication required');
      }

      const headers = {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json'
      };

      // Fetch test case details including studio configuration
      const detailsResponse = await fetch(`${API_URL}/test/test_case_details?case_id=${caseId}`, {
        method: 'GET',
        headers: headers
      });

      if (!detailsResponse.ok) {
        throw new Error(`Failed to fetch test case details: ${detailsResponse.status} ${detailsResponse.statusText}`);
      }

      const details = await detailsResponse.json();
      setTestCaseDetails(details);

      // Extract studio API domain
      const studioApiDomain = details?.studio?.api_domain;
      if (studioApiDomain) {
        setApiBaseUrl(`https://${studioApiDomain}`);
      }

      // Fetch test case code
      const codeResponse = await fetch(`${API_URL}/test/test_case_code?case_id=${caseId}`, {
        method: 'GET',
        headers: headers
      });

      if (!codeResponse.ok) {
        throw new Error(`Failed to fetch test case code: ${codeResponse.status} ${codeResponse.statusText}`);
      }

      const codeData = await codeResponse.json();
      
      // Update state with fetched data
      setCodeContent(codeData.content || '// No content available');
      setCurrentFile(codeData.file_path || 'main.js');
      
    } catch (error) {
      console.error('Error fetching test case data:', error);
      setError(error.message);
      setCodeContent(`// Error loading test case: ${error.message}\n// Test case for ${caseId}\nconsole.log('Hello, World!');`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch test case details when component mounts or caseId changes
  useEffect(() => {
    if (caseId) {
      fetchTestCaseDetails();
    }
  }, [caseId]);

  const handleCodeChange = (event) => {
    const newCode = event.target.value;
    setCodeContent(newCode);
  };

  const handleRunTest = async () => {
    if (!apiBaseUrl) {
      console.error('API Base URL not available');
      setTestResults({
        success: false,
        error: 'Studio API URL not configured',
        stderr: 'Unable to run test: Studio API URL not found'
      });
      return;
    }

    try {
      console.log('Running test:', currentFile);
      setIsRunning(true);
      setTestResults(null);

      // Get authentication token
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData?.session?.access_token) {
        throw new Error('Authentication required');
      }

      const headers = {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json'
      };

      // Make API call to run the test using dynamic apiBaseUrl
      const response = await fetch(`${apiBaseUrl}/run-tmp-playwright`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          testCode: codeContent,
          filePath: currentFile
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to run test: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      // Store results for display
      setTestResults(result);
      
      // Log the results
      console.log('Test execution result:', result);
      
      if (result.success) {
        console.log('✅ Test passed!');
      } else {
        console.log('❌ Test failed!');
        console.log('Exit code:', result.exit_code);
      }

    } catch (error) {
      console.error('Error running test:', error);
      setTestResults({
        success: false,
        error: error.message,
        stderr: error.message
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleDeleteTestCase = async () => {
    const confirmed = window.confirm('Are you sure you want to delete this test case? This action cannot be undone.');
    if (!confirmed) return;

    try {
      setIsDeleting(true);
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.access_token) {
        throw new Error('Authentication required');
      }
      const headers = {
        'Authorization': `Bearer ${sessionData.session.access_token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API_URL}/test/delete_test_case`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ case_id: caseId })
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to delete test case');
      }

      navigate('/test');
    } catch (err) {
      console.error('Error deleting test case:', err);
      alert(err.message || 'Failed to delete test case');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <React.Fragment>
      <NavBar />
      
      <Box
        component="main" 
        sx={{
          minHeight: '100vh',
          marginLeft: `${navbarWidth}px`,
          transition: 'margin-left 0.2s',
          width: `calc(100% - ${navbarWidth}px)`,
          backgroundColor: '#121212',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ height: '100vh', width: '100%' }}>
          <PanelGroup direction="horizontal" style={{ height: '100%' }}>
            {/* Left Panel - Code Editor (Full Height) */}
            <Panel defaultSize={50} minSize={30}>
              <Box sx={{ 
                height: '100%', 
                backgroundColor: '#1A1A1A',
                borderRight: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <CustomCodeEditor 
                  currentFile={currentFile}
                  codeContent={codeContent}
                  onCodeChange={handleCodeChange}
                  onRunTest={handleRunTest}
                  onDeleteTestCase={handleDeleteTestCase}
                  isLoading={isLoading}
                  isRunning={isRunning}
                  isDeleting={isDeleting}
                />
              </Box>
            </Panel>

            {/* Resize Handle */}
            <PanelResizeHandle 
              style={{
                width: '4px',
                backgroundColor: 'transparent',
                cursor: 'col-resize',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            />

            {/* Right Panel - Chrome Browser and Test Results */}
            <Panel defaultSize={50} minSize={30}>
              <PanelGroup direction="vertical" style={{ height: '100%' }}>
                {/* Top Panel - Remote Chrome Browser */}
                <Panel defaultSize={60} minSize={30}>
                  {apiBaseUrl ? (
                    <ChromeStreamerCDP 
                      width={1500}
                      height={800}
                      onUrlChange={(url) => console.log('URL changed:', url)}
                      apiBaseUrl={apiBaseUrl}
                      style={{
                        height: '100%',
                        width: '100%'
                      }}
                    />
                  ) : (
                    <Box sx={{ 
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#1A1A1A',
                      color: 'rgba(255, 255, 255, 0.5)'
                    }}>
                      <Typography variant="body2">Loading browser configuration...</Typography>
                    </Box>
                  )}
                </Panel>

                {/* Resize Handle */}
                <PanelResizeHandle 
                  style={{
                    height: '4px',
                    backgroundColor: 'transparent',
                    cursor: 'row-resize',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                />

                {/* Bottom Panel - Test Results */}
                <Panel defaultSize={40} minSize={20}>
                  <Box sx={{ 
                    height: '100%', 
                    backgroundColor: '#1A1A1A'
                  }}>
                    <TestResults 
                      results={testResults}
                      isRunning={isRunning}
                    />
                  </Box>
                </Panel>
              </PanelGroup>
            </Panel>
          </PanelGroup>
        </Box>
      </Box>
    </React.Fragment>
  );
}

export default TestCase; 